import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { SessionFrequency } from "@obg/mat.ui.common";
import { AlertButtonColor } from "@obg/mat.ui.core";
import { CommonState, MULTI_SESSION_LIMIT_MESSAGE_CONTEXT, MultiSessionLimitAction } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	CORE_ROUTE,
	CovidMeasuresConfigService,
	MessageService,
	MessageType,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { SessionLimitService } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { merge, Observable, Subscription } from "rxjs";
import { concatAll, map, take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import { SGA_SESSION_LIMIT_REMOVE_ALERT_ID, SGA_SESSION_LIMIT_UPDATE_ALERT_ID } from "./sga-session-limit.const";

@Component({
	selector: "obg-m-sga-session-limit-container",
	templateUrl: "./sga-session-limit.container.html",
	styleUrls: ["./sga-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [SessionLimitService],
})
export class SgaSessonLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-sga-session-limit") hostClass = true;

	@Input() @coerceBoolean() collapseDepositInfo = false;
	@Input() @coerceBoolean() autoFocusDisabled = false;
	@Input() @coerceBoolean() navigateToReturnUrl = false;

	@Output() navigate = new EventEmitter();

	showInputFields = false;
	isSettingBusy = false;
	isRemovingBusy = false;

	daily = SessionFrequency.Daily;
	weekly = SessionFrequency.Weekly;
	monthly = SessionFrequency.Monthly;
	hasCurrentLimits$: Observable<boolean>;
	covidMeasuresEnabled = this.covidMeasuresConfig.covidMeasuresEnabled();
	isJurisdictionSga$: Observable<boolean> | undefined;

	private returnUrl: string | undefined;
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		public formService: SessionLimitService,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private store: Store<CommonState>,
		private changeDetector: ChangeDetectorRef,
		private translatableAlertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private messageService: MessageService,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {
		this.hasCurrentLimits$ = this.formService.hasCurrentLimits$;
	}

	ngOnInit() {
		this.formService.init();

		const returnUrl$ = this.store
			.select<{ returnUrl: string | undefined } | undefined>(RouteSelector.getQueryParams)
			.pipe(tap((params) => (this.returnUrl = params?.returnUrl)));

		const removeSessionLimits$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.remove,
			})
			.pipe(
				concatAll(),
				tap((message) => {
					this.isRemovingBusy = false;
					this.formService.sessionLimitForm.reset();
					if (message.type === MessageType.Success) {
						this.updateSuccess("remove_success");
						return;
					}
					this.updateFail();
				})
			);

		const sessionLimitUpdate$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				concatAll(),
				tap((message) => {
					this.isSettingBusy = false;
					this.formService.sessionLimitForm.reset();
					if (message.type === MessageType.Success) {
						this.updateSuccess("update_success");
						return;
					}
					this.updateFail();
				})
			);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formService.sessionLimitForm?.reset();
			})
		);

		this.data$$ = merge(returnUrl$, sessionLimitUpdate$, removeSessionLimits$, resetForm$).subscribe(() =>
			this.changeDetector.markForCheck()
		);

		this.isFormDirty$$ = this.formService.sessionLimitForm.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.formService.sessionLimitForm.dirty)
		);

		this.isJurisdictionSga$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			map((jurisdiction) => jurisdiction === Jurisdiction.Sga)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	navigateToResponsibleGaming() {
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.responsibleGaming.sessionLimit));
		this.navigate.emit();
	}

	toggleInputFields() {
		this.showInputFields = true;
	}

	onSubmit() {
		this.translatableAlertService.open(
			{
				id: SGA_SESSION_LIMIT_UPDATE_ALERT_ID,
				title: "multi-session-limit.update.confirmation-title",
				message: `multi-session-limit.update.confirmation-caption`,
				buttons: [
					{
						color: AlertButtonColor.Primary,
						text: "general.close",
					},
					{
						text: "general.ok",
						handler: () => {
							this.store.dispatch(
								this.multiSessionLimitAction.update(this.formService.sessionLimitForm.value)
							);
							this.isSettingBusy = true;
							this.changeDetector.markForCheck();
						},
					},
				],
				dialogConfig: {
					disableClose: true,
				},
				hideClose: true,
				mustBeInteracted: true,
			},
			[TranslationGroup.General]
		);
	}

	onRemove() {
		this.translatableAlertService.open(
			{
				id: SGA_SESSION_LIMIT_REMOVE_ALERT_ID,
				title: "multi-session-limit.remove.confirmation-title",
				message: `multi-session-limit.remove.confirmation-caption`,
				buttons: [
					{
						color: AlertButtonColor.Primary,
						text: "general.close",
					},
					{
						text: "general.ok",
						handler: () => {
							this.isRemovingBusy = true;
							this.changeDetector.markForCheck();
							this.store.dispatch(this.multiSessionLimitAction.remove());
						},
					},
				],
				mustBeInteracted: true,
			},
			[TranslationGroup.Common]
		);
	}

	private updateFail() {
		this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private updateSuccess(suffix: string) {
		this.showInputFields = false;

		if (this.returnUrl && this.navigateToReturnUrl) {
			this.store.dispatch(RouteAction.navigateByUrl(this.returnUrl, { replaceUrl: true }));
			return;
		}

		this.snackBarService
			.openSnackBar$(
				{
					message: `multi-session-limit.m_multi_session_limit_${suffix}`,
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}
}
